import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, CalendarClock } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NotificationBell = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [lastCheck, setLastCheck] = useState(null);
    const lastCheckRef = useRef(null);

    useEffect(() => {
        const stored = localStorage.getItem('lastNotificationCheck');
        const initialLastCheck = stored ? new Date(stored) : new Date();
        setLastCheck(initialLastCheck);
        lastCheckRef.current = initialLastCheck;
        fetchNotifications();
        checkForNewAssignments();
        const interval = setInterval(() => {
            fetchNotifications();
            checkForNewAssignments();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_URL}/notifications`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(data);
            setUnreadCount(data.filter((n) => !n.read).length);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    };

    const checkForNewAssignments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/leads?filter=mine`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const myLeads = response.data;
            const now = new Date();
            const recentlyAssigned = myLeads.filter(lead => {
                if (!lead.assigned_at) return false;
                const assignedDate = new Date(lead.assigned_at);
                if (isNaN(assignedDate.getTime())) return false;
                const diffMinutes = (now - assignedDate) / 1000 / 60;
                return diffMinutes <= 5 && assignedDate > (lastCheckRef.current || new Date(0));
            });

            if (recentlyAssigned.length > 0) {
                const newNotifications = recentlyAssigned.map(lead => ({
                    id: `assignment-${lead.id}`,
                    message: `Lead "${lead.name}" was assigned to you`,
                    timestamp: lead.assigned_at,
                    read: false,
                    leadId: lead.id,
                    type: 'assignment'
                }));
                setNotifications(prev => [...newNotifications, ...prev]);
                setUnreadCount(prev => prev + newNotifications.length);
            }
        } catch (error) {
            console.error('Failed to check notifications:', error);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            if (notificationId) {
                await axios.put(`${API_URL}/notifications/${notificationId}/read`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            } else {
                await axios.put(`${API_URL}/notifications/read-all`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUnreadCount(0);
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            }
            const now = new Date();
            localStorage.setItem('lastNotificationCheck', now.toISOString());
            setLastCheck(now);
            lastCheckRef.current = now;
        } catch (e) {
            if (notificationId) {
                setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
            console.error('Failed to mark notifications read:', e);
        }
    };

    const handleNotificationClick = (n) => {
        if (n.thread_id && n.type === 'mention') {
            navigate('/inbox', { state: { openThreadId: n.thread_id } });
            markAsRead(n.id);
        } else if (n.type === 'follow_up_due' && n.lead_id) {
            navigate(`/leads/${n.lead_id}?view=activity`);
            markAsRead(n.id);
        } else if (n.leadId) {
            navigate(`/leads/${n.leadId}`);
            markAsRead(n.id);
        }
    };

    const clearNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/notifications/read-all`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications([]);
            setUnreadCount(0);
            const now = new Date();
            localStorage.setItem('lastNotificationCheck', now.toISOString());
            setLastCheck(now);
            lastCheckRef.current = now;
        } catch (e) {
            console.error('Failed to clear notifications:', e);
        }
    };

    return (
        <Popover onOpenChange={(open) => { if (!open) markAsRead(); }}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                >
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                            {unreadCount}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-sm">Notifications</h3>
                        {notifications.length > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={clearNotifications}
                                className="h-6 text-xs"
                            >
                                Clear all
                            </Button>
                        )}
                    </div>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">
                                No new notifications
                            </p>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-100 ${notification.read ? 'bg-slate-50' : notification.type === 'follow_up_due' ? 'bg-orange-50 border-orange-200' : 'bg-blue-50 border-blue-200'
                                        }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {notification.type === 'follow_up_due' && (
                                            <CalendarClock className="h-4 w-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                        )}
                                        <p className="text-sm text-slate-900 flex-1">{notification.message}</p>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">
                                        {(() => {
                                            const date = new Date(notification.created_at || notification.timestamp);
                                            return isNaN(date.getTime()) ? 'Recently' : format(date, 'PPp');
                                        })()}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default NotificationBell;
